
import { useState, useEffect, useCallback, useMemo } from "react";
import { getCalendarEvents, getCalendarConnection } from "@/lib/calendar";
import { fetchHostAvailability } from "@/lib/calendar/host-availability";
import { toast } from "@/hooks/use-toast";
import { type AvailabilitySettings, type TimeSlot, fetchHostAvailabilitySettings, getAvailableTimeSlots, filterAvailableSlots } from "@/lib/availability";
import type { EnergyBlockType } from "@/lib/energy";
import { supabase } from "@/integrations/supabase/client"; 
import { isDayAvailable as checkDayIsAvailable, logAvailabilityDebug, checkAvailabilitySystemHealth } from "@/lib/availability-utils";

// Enhanced cache for connection status with better performance
const connectionStatusCache = new Map<string, {
  status: boolean;
  timestamp: number;
  promise?: Promise<boolean>;
}>();

// Cache for day availability with date-specific keys
const dayAvailabilityCache = new Map<string, {
  isAvailable: boolean;
  timestamp: number;
}>();

// Cache for host settings with longer duration
const hostSettingsCache = new Map<string, {
  settings: AvailabilitySettings;
  timestamp: number;
}>();

const CONNECTION_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes
const DAY_AVAILABILITY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const HOST_SETTINGS_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes - much longer for settings
const MAX_RETRIES = 2; // Reduced from 3 for faster failure
const RETRY_DELAY = 500; // Reduced from 1000ms

interface UseCalendarSlotsProps {
  selectedDate: Date;
  duration?: number;
  energyPreference?: EnergyBlockType;
  hostUserId?: string;
  defaultSettings?: Partial<AvailabilitySettings & {
    energyPreference?: EnergyBlockType;
    duration?: number;
  }>;
  timezone?: string;
  includeRawData?: boolean;
  troubleshootMode?: boolean;
  wakeTime?: number;
  availabilityWindow?: { startTime: string; endTime: string };
  excludeEventId?: string; // NEW: exclude specific calendar event (for reschedule mode)
}

interface CalendarSlotsData {
  hasConnection: boolean;
  hostSettings: AvailabilitySettings;
  busyEvents: any[];
  availableSlots: string[];
  unavailableSlots: any[];
  systemHealth?: any;
}

// Helper function for faster retry with exponential backoff
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = MAX_RETRIES,
  delay: number = RETRY_DELAY,
  operation: string = 'operation'
): Promise<T> {
  let lastError: Error;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      console.warn(`[retryWithBackoff] ${operation} attempt ${i + 1} failed:`, error);
      
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(1.5, i))); // Reduced backoff
      }
    }
  }
  
  throw lastError!;
}

export function useCalendarSlots({
  selectedDate,
  duration = 30,
  energyPreference = "focus" as EnergyBlockType,
  hostUserId,
  defaultSettings,
  timezone,
  includeRawData = false,
  troubleshootMode = false,
  wakeTime = 7,
  availabilityWindow,
  excludeEventId // NEW: exclude specific calendar event (for reschedule mode)
}: UseCalendarSlotsProps) {
  const [data, setData] = useState<CalendarSlotsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataReady, setDataReady] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [dateAvailabilityMap, setDateAvailabilityMap] = useState<Map<string, boolean>>(new Map());
  
  // Memoize stable values to prevent re-renders
  const stableSelectedDate = useMemo(() => selectedDate.toDateString(), [selectedDate]);
  const stableHostUserId = useMemo(() => {
    // Handle empty, null, or invalid user IDs
    if (!hostUserId || hostUserId.trim() === '' || hostUserId === 'undefined' || hostUserId === 'null') {
      return 'debug'; // Use debug mode for invalid user IDs
    }
    return hostUserId;
  }, [hostUserId]);
  const stableTimezone = useMemo(() => timezone || 'UTC', [timezone]);
  
  // Enhanced connection check with parallel optimization
  const checkConnectionStatus = useCallback(async (userId: string): Promise<boolean> => {
    if (userId === 'debug') return true;
    
    const cacheKey = userId;
    const now = Date.now();
    
    // Check cache first
    const cached = connectionStatusCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < CONNECTION_CACHE_DURATION) {
      return cached.status;
    }
    
    // If there's already a pending request, wait for it
    if (cached?.promise) {
      return cached.promise;
    }
    
    // Create new request with comprehensive error handling
    const promise = (async () => {
      try {
        // Use getCalendarConnection instead of checkHostCalendarConnection
        const connection = await getCalendarConnection();
        const status = !!connection;
        
        // Cache the successful result
        connectionStatusCache.set(cacheKey, {
          status,
          timestamp: now
        });
        
        return status;
      } catch (error) {
        console.error(`[checkConnectionStatus] Connection check failed for ${userId}:`, error);
        // Cache negative result for shorter time to allow faster retry
        connectionStatusCache.set(cacheKey, {
          status: false,
          timestamp: now - (CONNECTION_CACHE_DURATION / 2)
        });
        return false;
      }
    })();
    
    // Cache the promise temporarily to prevent duplicate requests
    connectionStatusCache.set(cacheKey, {
      status: false, // Placeholder
      timestamp: now,
      promise
    });
    
    const result = await promise;
    
    // Update cache with final result (remove promise)
    connectionStatusCache.set(cacheKey, {
      status: result,
      timestamp: now
    });
    
    return result;
  }, []);

  // Enhanced host settings fetch with caching
  const fetchHostSettings = useCallback(async (userId: string): Promise<AvailabilitySettings> => {
    if (userId === 'debug') {
      return {
        bufferBefore: defaultSettings?.bufferBefore || 15,
        bufferAfter: defaultSettings?.bufferAfter || 15,
        minNoticeTime: defaultSettings?.minNoticeTime || 120,
      };
    }

    const cacheKey = userId;
    const now = Date.now();
    
    // Check cache first
    const cached = hostSettingsCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < HOST_SETTINGS_CACHE_DURATION) {
      return cached.settings;
    }

    try {
      const settings = await retryWithBackoff(
        () => fetchHostAvailabilitySettings(userId),
        2,
        300,
        'host settings fetch'
      );
      
      // Cache the result
      hostSettingsCache.set(cacheKey, {
        settings,
        timestamp: now
      });
      
      return settings;
    } catch (error) {
      console.error(`[fetchHostSettings] Error fetching settings for ${userId}:`, error);
      // Return default settings on error
      const defaultSettingsValue = {
        bufferBefore: defaultSettings?.bufferBefore || 15,
        bufferAfter: defaultSettings?.bufferAfter || 15,
        minNoticeTime: defaultSettings?.minNoticeTime || 120,
      };
      
      // Cache default settings for shorter time
      hostSettingsCache.set(cacheKey, {
        settings: defaultSettingsValue,
        timestamp: now - (HOST_SETTINGS_CACHE_DURATION / 2)
      });
      
      return defaultSettingsValue;
    }
  }, [defaultSettings]);

  // Function to check availability for a specific date and cache it
  const checkAndCacheDayAvailability = useCallback(async (date: Date, userId: string): Promise<boolean> => {
    if (!userId || userId === 'debug') {
      return true;
    }

    const cacheKey = `${userId}_${date.toDateString()}`;
    const now = Date.now();
    
    // Check cache first
    const cached = dayAvailabilityCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < DAY_AVAILABILITY_CACHE_DURATION) {
      return cached.isAvailable;
    }

    try {
      const isAvailable = await checkDayIsAvailable(userId, date, supabase);
      
      // Cache the result
      dayAvailabilityCache.set(cacheKey, {
        isAvailable,
        timestamp: now
      });
      
      // Update component state cache
      setDateAvailabilityMap(prev => new Map(prev.set(date.toDateString(), isAvailable)));
      
      logAvailabilityDebug(userId, date, isAvailable, 'checkAndCacheDayAvailability');
      return isAvailable;
    } catch (error) {
      console.error(`[checkAndCacheDayAvailability] Error checking availability for ${date.toDateString()}:`, error);
      // Cache negative result for shorter time
      dayAvailabilityCache.set(cacheKey, {
        isAvailable: false,
        timestamp: now - (DAY_AVAILABILITY_CACHE_DURATION / 2)
      });
      
      // Update component state cache
      setDateAvailabilityMap(prev => new Map(prev.set(date.toDateString(), false)));
      return false;
    }
  }, []);

  // Pre-cache availability for a range of dates (optimized batch processing)
  const preCacheAvailability = useCallback(async (centerDate: Date, userId: string) => {
    if (!userId || userId === 'debug') return;

    const startDate = new Date(centerDate);
    startDate.setDate(startDate.getDate() - 7); // Reduced from 15 days
    const endDate = new Date(centerDate);
    endDate.setDate(endDate.getDate() + 21); // Reduced from 45 days

    const promises: Promise<void>[] = [];
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const currentDate = new Date(date);
      const cacheKey = `${userId}_${currentDate.toDateString()}`;
      
      // Skip if already cached and still valid
      const cached = dayAvailabilityCache.get(cacheKey);
      const now = Date.now();
      if (cached && (now - cached.timestamp) < DAY_AVAILABILITY_CACHE_DURATION) {
        continue;
      }
      
      promises.push(
        checkAndCacheDayAvailability(currentDate, userId).then(() => {}).catch(() => {})
      );
    }

    // Execute in smaller batches to avoid overwhelming the system
    const batchSize = 3; // Reduced from 5
    for (let i = 0; i < promises.length; i += batchSize) {
      const batch = promises.slice(i, i + batchSize);
      await Promise.all(batch);
      // Small delay between batches to prevent rate limiting
      if (i + batchSize < promises.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
  }, [checkAndCacheDayAvailability]);
  
  // Enhanced refetch function with system health check
  const refetch = useCallback(async () => {
    console.log(`[useCalendarSlots] Refetching data (attempt ${retryCount + 1})`);
    setLoading(true);
    setDataReady(false);
    setData(null);
    setError(null);
    setRetryCount(prev => prev + 1);
    
    // Clear caches to force fresh check
    if (stableHostUserId) {
      connectionStatusCache.delete(stableHostUserId);
      hostSettingsCache.delete(stableHostUserId);
      // Clear day availability cache for this user
      for (const [key] of dayAvailabilityCache.entries()) {
        if (key.startsWith(stableHostUserId)) {
          dayAvailabilityCache.delete(key);
        }
      }
      setDateAvailabilityMap(new Map());
    }
    
    // System health check will be called in the main effect, not here to avoid duplicates
  }, [retryCount, stableHostUserId, troubleshootMode]);

  // MASSIVELY OPTIMIZED data fetching with parallel execution
  useEffect(() => {
    let isCancelled = false;
    
    async function fetchAllDataParallel() {
      if (isCancelled) return;
      
      try {
        setLoading(true);
        setError(null);
        setDataReady(false);
        
        // console.log(`[useCalendarSlots] ===== PARALLEL DATA FETCH START =====`);
        // console.log(`[useCalendarSlots] Date: ${selectedDate.toDateString()}`);
        // console.log(`[useCalendarSlots] User: ${stableHostUserId}`);
        
        // Early exit for invalid user IDs that couldn't be converted to debug
        if (!stableHostUserId || stableHostUserId === '') {
          console.log(`[useCalendarSlots] Invalid user ID detected, setting defaults`);
          setData({
            hasConnection: false,
            hostSettings: {
              bufferBefore: defaultSettings?.bufferBefore || 15,
              bufferAfter: defaultSettings?.bufferAfter || 15,
              minNoticeTime: defaultSettings?.minNoticeTime || 120,
            },
            busyEvents: [],
            availableSlots: [],
            unavailableSlots: []
          });
          setDataReady(true);
          setLoading(false);
          return;
        }
        
        // STEP 1: Execute all independent operations in parallel
        const parallelOperations = await Promise.allSettled([
          // Operation 1: Check calendar connection
          checkConnectionStatus(stableHostUserId),
          
          // Operation 2: Fetch host settings (with enhanced caching)
          fetchHostSettings(stableHostUserId),
          
          // Operation 3: Pre-cache day availability (non-blocking)
          preCacheAvailability(selectedDate, stableHostUserId),
          
          // Operation 4: System health check (if needed) - only call once
          troubleshootMode && stableHostUserId !== 'debug' && !systemHealth
            ? checkAvailabilitySystemHealth(stableHostUserId, supabase)
            : Promise.resolve(systemHealth)
        ]);

        if (isCancelled) return;

        // Extract results from parallel operations
        const hasConnection = parallelOperations[0].status === 'fulfilled' 
          ? parallelOperations[0].value 
          : false;
        
        const hostSettings = parallelOperations[1].status === 'fulfilled' 
          ? parallelOperations[1].value 
          : {
              bufferBefore: defaultSettings?.bufferBefore || 15,
              bufferAfter: defaultSettings?.bufferAfter || 15,
              minNoticeTime: defaultSettings?.minNoticeTime || 120,
            };
        
        const systemHealthResult = parallelOperations[3].status === 'fulfilled' 
          ? parallelOperations[3].value 
          : null;

        console.log(`[useCalendarSlots] Parallel operations completed - Connection: ${hasConnection}`);

        // STEP 2: Check day availability (now likely cached)
        const selectedDateKey = selectedDate.toDateString();
        const isDayAvailableForSelected = dateAvailabilityMap.get(selectedDateKey) ?? 
          await checkAndCacheDayAvailability(selectedDate, stableHostUserId);

        if (isCancelled) return;

        // STEP 3: Fetch calendar events (only if needed)
        let busyEvents: any[] = [];
        if ((hasConnection || stableHostUserId === 'debug') && (isDayAvailableForSelected || troubleshootMode)) {
          const dayStart = new Date(selectedDate);
          dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(selectedDate);
          dayEnd.setHours(23, 59, 59, 999);
          
          if (stableHostUserId === 'debug') {
            busyEvents = [];
          } else if (stableHostUserId) {
            try {
              const response = await retryWithBackoff(
                async () => {
                  return await fetchHostAvailability(
                    stableHostUserId,
                    dayStart.toISOString(),
                    dayEnd.toISOString(),
                    stableTimezone
                  );
                },
                2,
                300,
                'host availability fetch'
              );
              
              if (response.error) {
                console.warn('[useCalendarSlots] Calendar error:', response.error);
                if (response.error.includes('Failed to refresh access token') || 
                    response.error.includes('Host calendar connection expired')) {
                  setError("Host's calendar connection has expired. They need to reconnect their calendar.");
                } else {
                  setError("Failed to load host calendar events");
                }
              }
              
              busyEvents = response.items || [];
            } catch (err) {
              console.error('[useCalendarSlots] Calendar events error:', err);
              busyEvents = [];
            }
          }
        }

        // Exclude specific event if provided (for reschedule mode)
        if (excludeEventId) {
          const originalCount = busyEvents.length;
          busyEvents = busyEvents.filter((event: any) => event.id !== excludeEventId);
          console.log(`[useCalendarSlots] Excluded event ${excludeEventId} from busy events (${originalCount} -> ${busyEvents.length})`);
        }

        console.log(`[useCalendarSlots] Busy events: ${busyEvents.length}`);

        if (isCancelled) return;
        
        // STEP 4: Calculate available slots (optimized)
        let availableSlots: string[] = [];
        let unavailableSlots: any[] = [];
        
        if ((hasConnection || stableHostUserId === 'debug') && (isDayAvailableForSelected || troubleshootMode)) {
          try {
            const slots = await retryWithBackoff(
              async () => {
                return await getAvailableTimeSlots({
                  date: selectedDate,
                  events: busyEvents,
                  settings: hostSettings as any,
                  energyPreference: energyPreference,
                  duration,
                  hostUserId: stableHostUserId,
                  timezone: stableTimezone,
                  includeUnavailable: includeRawData || troubleshootMode,
                  wakeTime,
                  availabilityWindow
                });
              },
              2,
              300,
              'available time slots calculation'
            );
            
            // Enhanced slot filtering logic with fallbacks
            const energyFilteredSlots = filterAvailableSlots(slots, { 
              energyPreference: energyPreference
            }).filter(slot => slot.isAvailable).map(slot => slot.startTime);
            
            const allAvailableSlots = slots
              .filter(slot => slot.isAvailable)
              .map(slot => slot.startTime);
            
            // Smart fallback logic
            if (energyFilteredSlots.length === 0 && 
                energyPreference && 
                energyPreference !== 'ignore' && 
                energyPreference !== 'any' && 
                energyPreference.toLowerCase() !== 'any') {
              console.log(`[useCalendarSlots] No slots with ${energyPreference} energy, using all available`);
              availableSlots = allAvailableSlots;
            } else {
              availableSlots = energyFilteredSlots;
            }
            
            if (includeRawData || troubleshootMode) {
              unavailableSlots = slots.filter(slot => !slot.isAvailable);
            }
            
            console.log(`[useCalendarSlots] Final available slots: ${availableSlots.length}`);
          } catch (err) {
            console.error("[useCalendarSlots] Error calculating slots:", err);
            setError("Failed to calculate available time slots");
          }
        }
        
        if (isCancelled) return;
        
        // Set consolidated data with enhanced information
        const consolidatedData: CalendarSlotsData = {
          hasConnection,
          hostSettings,
          busyEvents,
          availableSlots,
          unavailableSlots,
          systemHealth: systemHealthResult
        };
        
        setData(consolidatedData);
        setDataReady(true);
        setRetryCount(0); // Reset retry count on success
        
        // console.log(`[useCalendarSlots] ===== PARALLEL DATA FETCH SUCCESS =====`);
        // console.log(`[useCalendarSlots] Available slots: ${availableSlots.length}`);
        // console.log(`[useCalendarSlots] Day available: ${isDayAvailableForSelected}`);
        // console.log(`[useCalendarSlots] Connection: ${hasConnection}`);
        
      } catch (err) {
        if (!isCancelled) {
          console.error('[useCalendarSlots] Critical error in parallel data fetch:', err);
          setError(`Failed to load calendar data: ${err instanceof Error ? err.message : 'Unknown error'}`);
          
          // Show user-friendly error toast in troubleshoot mode
          if (troubleshootMode) {
            toast({
              title: "Calendar Data Error",
              description: `Failed to load calendar data. Attempt ${retryCount + 1}.`,
              variant: "destructive"
            });
          }
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    }
    
    // Start fetch immediately without delay for better perceived performance
    fetchAllDataParallel();
    
    return () => {
      isCancelled = true;
    };
  }, [
    stableSelectedDate, 
    stableHostUserId, 
    stableTimezone, 
    duration, 
    energyPreference, 
    JSON.stringify(defaultSettings),
    includeRawData, 
    troubleshootMode, 
    wakeTime,
    availabilityWindow,
    excludeEventId,
    checkConnectionStatus,
    fetchHostSettings,
    preCacheAvailability,
    retryCount,
    dateAvailabilityMap,
    checkAndCacheDayAvailability
  ]);

  // Synchronous isDayAvailable function that uses cached data
  const isDayAvailable = useCallback((date: Date): boolean => {
    if (!stableHostUserId || stableHostUserId === 'debug') {
      return true;
    }
    
    const dateKey = date.toDateString();
    const cachedResult = dateAvailabilityMap.get(dateKey);
    
    // If we have cached data, use it
    if (cachedResult !== undefined) {
      return cachedResult;
    }
    
    // Check the global cache as fallback
    const globalCacheKey = `${stableHostUserId}_${dateKey}`;
    const globalCached = dayAvailabilityCache.get(globalCacheKey);
    if (globalCached) {
      const now = Date.now();
      if ((now - globalCached.timestamp) < DAY_AVAILABILITY_CACHE_DURATION) {
        // Update local state
        setDateAvailabilityMap(prev => new Map(prev.set(dateKey, globalCached.isAvailable)));
        return globalCached.isAvailable;
      }
    }
    
    // If no cache data available, trigger async check and return optimistic true
    // This prevents all dates from being disabled while data loads
    checkAndCacheDayAvailability(date, stableHostUserId).catch(() => {});
    return true; // Optimistic default
  }, [stableHostUserId, dateAvailabilityMap, checkAndCacheDayAvailability]);

  return {
    loading,
    hasConnection: data?.hasConnection ?? null,
    availableSlots: dataReady ? (data?.availableSlots || []) : [],
    unavailableSlots: includeRawData ? (data?.unavailableSlots || []) : undefined,
    error,
    isDayAvailable,
    noAvailability: false,
    refetch,
    hostSettings: data?.hostSettings,
    retryCount,
    systemHealth: data?.systemHealth
  };
}
